'use client';

import * as React from 'react';

import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import {
  ScrollArea as ShadcnScrollArea,
  ScrollBar as ShadcnScrollBar,
} from '@/hen-ui/shadcn/scroll-area';
import { cn } from '@/hen-ui/utils';

/**
 * Which axes can scroll inside the area.
 *
 * - `vertical` (default): vertical `ScrollBar` only -- the bar auto-rendered by
 *   the underlying shadcn primitive.
 * - `horizontal`: adds a horizontal `ScrollBar`. (The vertical bar from shadcn
 *   only surfaces if content actually overflows vertically.)
 * - `both`: adds the horizontal `ScrollBar` alongside the auto-rendered
 *   vertical one.
 */
export type ScrollAreaOrientation = 'vertical' | 'horizontal' | 'both';

export interface ScrollAreaProps
  extends React.ComponentProps<typeof ShadcnScrollArea> {
  /**
   * Which axis (or axes) the area scrolls on. The component renders the
   * appropriate `ScrollBar`(s) so consumers never have to compose them.
   *
   * @default 'vertical'
   */
  orientation?: ScrollAreaOrientation;
}

export type ScrollBarProps = React.ComponentProps<typeof ShadcnScrollBar>;

/**
 * `ScrollBar` is re-exported for advanced composition scenarios. Standard
 * usage should rely on the `orientation` prop on `ScrollArea` instead.
 */
export const ScrollBar = ShadcnScrollBar;

/**
 * ScrollArea component following HEN Design System standards.
 *
 * Thin wrapper around the shadcn `ScrollArea` primitive that adds the HEN
 * catalog `data-hen-id` attribute, `overflow-hidden` on the root to prevent
 * content bleed in flex layouts, and an `orientation` prop that owns the
 * `ScrollBar` composition so consumers never assemble bars manually.
 *
 * ```tsx
 * <ScrollArea className="h-[288px] w-[192px]">{verticalContent}</ScrollArea>
 *
 * <ScrollArea orientation="horizontal" className="w-[384px]">
 *   <div className="flex w-max">{horizontalContent}</div>
 * </ScrollArea>
 *
 * <ScrollArea orientation="both" className="h-[288px] w-[384px]">
 *   {gridContent}
 * </ScrollArea>
 * ```
 *
 * ## Sizing requirements
 *
 * The root **must** have an explicit height (and width, for horizontal) so the
 * viewport's `size-full` can clip overflowing content. When used inside a flex
 * parent (`flex-1`), pair it with `min-h-0` (vertical) or `min-w-0`
 * (horizontal) so the area can shrink below its content size.
 *
 * ## Thumb visibility (`type`)
 *
 * Inherits Radix semantics:
 * - `hover` (default): thumb visible while hovering the scroll area.
 * - `scroll`: thumb visible while actively scrolling.
 * - `auto`: thumb visible whenever content overflows.
 * - `always`: thumb always visible.
 *
 * See
 * {@link https://ui.shadcn.com/docs/components/scroll-area shadcn Scroll Area}
 * and
 * {@link https://www.radix-ui.com/primitives/docs/components/scroll-area Radix Scroll Area}
 * for the full API.
 */
export const ScrollArea = ({
  orientation = 'vertical',
  className,
  children,
  ...props
}: ScrollAreaProps) => (
  <ShadcnScrollArea
    data-hen-id={COMPONENT_IDS.ScrollArea}
    data-orientation={orientation}
    className={cn('overflow-hidden', className)}
    {...props}
  >
    {children}
    {orientation !== 'vertical' && <ScrollBar orientation='horizontal' />}
  </ShadcnScrollArea>
);
ScrollArea.displayName = 'ScrollArea';
